import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  Paper,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useNamedItemMap } from './useNamedItemMap';
import AddIcon from '@material-ui/icons/Add';
import type { Lambda } from 'config-editor-common';
import { LambdaItemHeader } from './LambdaComponents/shared/LambdaItemHeader';
import { LAMBDA_ORANGE_LIGHT } from './LambdaComponents/shared/constants';
import { DEFAULT_SNAPSTART_ENABLED, DEFAULT_SNAP_ALIAS_ENABLED } from 'config-editor-common';
import { PhxIamPolicyMultiSelect } from './PhxIamPolicyMultiSelect';
import { IamPolicyMultiSelect } from './IamPolicyMultiSelect';
import {
  DEFAULT_VPC_ACCESS_STEP_FUNCTION,
  SecurityGroupsSelector,
} from './LambdaComponents/phx-api-v2/SecurityGroupsSelector';
import { CapacityProviderSelect } from './LambdaComponents/phx-api-v2/CapacityProviderSelect';

const LMI_INCOMPATIBLE_REASON = 'Not supported for LMI lambdas (capacity provider set).';

const useStyles = makeStyles(theme => ({
  section: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  lambdaItem: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `2px solid ${LAMBDA_ORANGE_LIGHT}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  addAnotherButton: {
    marginTop: theme.spacing(2),
  },
}));

export interface SimpleLambdaListEditorProps {
  lambdas: Record<string, Lambda>;
  onChange: (lambdas: Record<string, Lambda>) => void;
  sharedIamPolicyNames?: string[];
  onSharedIamPolicyNamesChange?: (value: string[]) => void;
  title?: string;
  description?: string;
  showPaper?: boolean;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  emptyStateAddButtonText?: string;
}

export const SimpleLambdaListEditor = ({
  lambdas,
  onChange,
  sharedIamPolicyNames = [],
  onSharedIamPolicyNamesChange,
  title = 'Lambdas',
  description,
  showPaper = true,
  emptyStateTitle = 'No lambdas configured',
  emptyStateDescription,
  emptyStateAddButtonText = 'Add First Lambda',
}: SimpleLambdaListEditorProps) => {
  const classes = useStyles();
  const { add, remove, rename, registerRef, registerContainer } = useNamedItemMap<Lambda>(
    lambdas,
    onChange,
    () => ({ lambda_handler: '', lambda_source: '' }),
  );

  const updateLambda = (
    name: string,
    field:
      | 'lambda_handler'
      | 'lambda_source'
      | 'timeout'
      | 'memory_size'
      | 'sg_ids'
      | 'iam_policy_names'
      | 'iam_policy_names_custom'
      | 'param_and_secrets_ext_enabled'
      | 'snapstart'
      | 'snap_alias'
      | 'alarms_sns_topic'
      | 'capacity_provider'
      | 'lambda_integration_arns',
    value: string | number | string[] | boolean,
  ) => {
    onChange({
      ...lambdas,
      [name]: {
        ...lambdas[name],
        [field]: value,
      },
    });
  };

  const content = (
    <>
      <Box className={classes.header}>
        <Typography variant="h6">{title}</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={add}
          startIcon={<AddIcon />}
          style={{ backgroundColor: '#4caf50', color: '#FFFFFF' }}
        >
          Add Lambda
        </Button>
      </Box>
      {description && (
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
          {description}
        </Typography>
      )}
      {onSharedIamPolicyNamesChange && (
        <Box mb={2}>
          <PhxIamPolicyMultiSelect
            value={sharedIamPolicyNames}
            onChange={onSharedIamPolicyNamesChange}
            label="Shared IAM Policy Names"
            helperText="Policies attached to all managed lambdas in this group."
            variant="outlined"
            size="small"
          />
        </Box>
      )}

      {Object.keys(lambdas).length === 0 ? (
        <div className={classes.emptyState}>
          <Typography variant="body1">{emptyStateTitle}</Typography>
          {emptyStateDescription && (
            <Typography variant="body2" style={{ marginTop: 8 }}>
              {emptyStateDescription}
            </Typography>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={add}
            style={{
              marginTop: 16,
              backgroundColor: '#4caf50',
              color: '#FFFFFF',
            }}
          >
            {emptyStateAddButtonText}
          </Button>
        </div>
      ) : (
        <>
          {Object.entries(lambdas).map(([name, lambda]) => {
            const lmiEnabled = !!lambda.capacity_provider;
            return (
              <div key={name} className={classes.lambdaItem} ref={registerContainer(name)}>
                <LambdaItemHeader
                  ref={registerRef(name)}
                  name={name}
                  onRename={rename}
                  onDelete={remove}
                />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Lambda Handler"
                      value={lambda.lambda_handler || ''}
                      onChange={e => updateLambda(name, 'lambda_handler', e.target.value)}
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="e.g., com.lexipol.example.Handler::handleRequest"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Lambda Source"
                      value={lambda.lambda_source || ''}
                      onChange={e => updateLambda(name, 'lambda_source', e.target.value)}
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="e.g., solutions-api-lambdas"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Timeout (seconds)"
                      type="number"
                      value={lambda.timeout ?? ''}
                      onChange={e => {
                        const v = e.target.value.trim();
                        updateLambda(name, 'timeout', v === '' ? '' : Number(v));
                      }}
                      fullWidth
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 1, max: 900 }}
                      placeholder="900"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Memory Size (MB)"
                      type="number"
                      value={lambda.memory_size ?? ''}
                      onChange={e => {
                        const v = e.target.value.trim();
                        updateLambda(name, 'memory_size', v === '' ? '' : Number(v));
                      }}
                      fullWidth
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 128, step: 64 }}
                      placeholder="1024"
                    />
                  </Grid>
                  <CapacityProviderSelect
                    value={lambda.capacity_provider ?? ''}
                    onChange={value => updateLambda(name, 'capacity_provider', value)}
                    gridItem
                  />
                  <Grid item xs={12} md={4}>
                    <Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="primary"
                            checked={
                              lmiEnabled ? false : lambda.snapstart ?? DEFAULT_SNAPSTART_ENABLED
                            }
                            onChange={e => {
                              const checked = e.target.checked;
                              // SnapStart requires the snap alias; turning it on forces snap_alias true.
                              onChange({
                                ...lambdas,
                                [name]: {
                                  ...lambdas[name],
                                  snapstart: checked,
                                  ...(checked ? { snap_alias: true } : {}),
                                },
                              });
                            }}
                            disabled={lmiEnabled}
                          />
                        }
                        label="SnapStart"
                      />
                      <FormHelperText>
                        {lmiEnabled
                          ? LMI_INCOMPATIBLE_REASON
                          : 'Per-lambda setting. When enabled, Lambda uses SnapStart for supported runtimes (typically Java) for faster cold starts.'}
                      </FormHelperText>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="primary"
                            checked={
                              // Forced on for LMI lambdas and when SnapStart is enabled;
                              // otherwise freely editable (can be on with SnapStart off).
                              lmiEnabled || (lambda.snapstart ?? DEFAULT_SNAPSTART_ENABLED)
                                ? true
                                : lambda.snap_alias ?? DEFAULT_SNAP_ALIAS_ENABLED
                            }
                            onChange={e => updateLambda(name, 'snap_alias', e.target.checked)}
                            // Locked on for LMI lambdas or while SnapStart is enabled.
                            disabled={lmiEnabled || (lambda.snapstart ?? DEFAULT_SNAPSTART_ENABLED)}
                          />
                        }
                        label="Create snap alias"
                      />
                      <FormHelperText>
                        {(() => {
                          if (lmiEnabled) {
                            return 'Required for LMI lambdas (capacity provider set).';
                          }
                          if (lambda.snapstart ?? DEFAULT_SNAPSTART_ENABLED) {
                            return 'Required while SnapStart is enabled.';
                          }
                          return 'Creates a Lambda alias. Can be enabled independently of SnapStart.';
                        })()}
                      </FormHelperText>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Alarms SNS topic"
                      value={lambda.alarms_sns_topic ?? ''}
                      onChange={e => updateLambda(name, 'alarms_sns_topic', e.target.value)}
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="e.g., lexipub-cw-alarms-topic"
                      helperText="SNS topic name (not full ARN) for CloudWatch alarms for this lambda."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <PhxIamPolicyMultiSelect
                      value={lambda.iam_policy_names ?? []}
                      onChange={value => updateLambda(name, 'iam_policy_names', value)}
                      label="IAM Policy Names"
                      helperText="Policy names from phoenix policy library to attach to this lambda."
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" style={{ marginBottom: 4 }}>
                      Custom IAM Policy Names
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      style={{ fontSize: '0.75rem', marginBottom: 8 }}
                    >
                      Enter custom IAM policy names that are not available in the standard policies.
                    </Typography>
                    <IamPolicyMultiSelect
                      value={lambda.iam_policy_names_custom ?? []}
                      onChange={value => updateLambda(name, 'iam_policy_names_custom', value)}
                      label="Custom IAM Policy Names"
                      helperText="Enter custom IAM policy names"
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={
                            lmiEnabled ? false : lambda.param_and_secrets_ext_enabled ?? false
                          }
                          onChange={e =>
                            updateLambda(name, 'param_and_secrets_ext_enabled', e.target.checked)
                          }
                          disabled={lmiEnabled}
                        />
                      }
                      label="Parameters & Secrets extension"
                    />
                    <FormHelperText>
                      {lmiEnabled
                        ? LMI_INCOMPATIBLE_REASON
                        : 'Enables the AWS Parameters and Secrets Lambda extension layer (SSM Parameter Store / Secrets Manager). Datadog APM is always applied by the step-function module.'}
                    </FormHelperText>
                  </Grid>
                  <Grid item xs={12}>
                    <SecurityGroupsSelector
                      value={lambda.sg_ids ?? []}
                      onChange={value => updateLambda(name, 'sg_ids', value)}
                      defaultVpcAccessSgId={DEFAULT_VPC_ACCESS_STEP_FUNCTION}
                      label="Security Groups"
                      helperText={
                        lmiEnabled
                          ? LMI_INCOMPATIBLE_REASON
                          : 'Default VPC access uses lexipub_lambdas_sg for Step Function managed lambdas; MongoDB options match the API editor.'
                      }
                      disabled={lmiEnabled}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" style={{ marginBottom: 4 }}>
                      Lambda Service Integrations
                    </Typography>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={[]}
                      value={lambda.lambda_integration_arns ?? []}
                      onChange={(_e, newValue) =>
                        updateLambda(
                          name,
                          'lambda_integration_arns',
                          newValue.filter(Boolean) as string[],
                        )
                      }
                      renderInput={params => (
                        <TextField
                          {...params}
                          variant="outlined"
                          size="small"
                          placeholder="arn:aws:lambda:${region}:${account_id}:function:my_lambda"
                        />
                      )}
                    />
                    <FormHelperText>
                      ARNs of other lambdas this lambda may invoke. Terraform adds both the base ARN
                      and the <code>:*</code> wildcard form for each entry.
                    </FormHelperText>
                  </Grid>
                </Grid>
              </div>
            );
          })}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={add}
            className={classes.addAnotherButton}
          >
            Add another lambda
          </Button>
        </>
      )}
    </>
  );

  if (showPaper) {
    return <Paper className={classes.section}>{content}</Paper>;
  }

  return <div>{content}</div>;
};
